/* eslint-disable no-unused-expressions */
/* eslint-disable no-plusplus */

import React, { useMemo, useState, useEffect } from 'react';

export default function DobInput({ locale = 'en-US', onChange }) {
    const [month, setMonth] = useState('');
    const [day, setDay] = useState('');
    const [year, setYear] = useState('');

    /* ---------- Localized month names ---------- */
    const months = useMemo(() => {
        const fmt = new Intl.DateTimeFormat(locale, { month: 'long' });
        return Array.from({ length: 12 }, (_, i) => ({
            index: i + 1,
            name: fmt.format(new Date(2000, i, 1)).toLowerCase()
        }));
    }, [locale]);

    function jaroWinkler(a, b, options = {}) {
        const {
            prefixScale = 0.1, // Winkler scaling factor (standard = 0.1)
            maxPrefix = 4,
            ignoreCase = true
        } = options;

        if (!a || !b) return 0;
        if (ignoreCase) {
            // eslint-disable-next-line no-param-reassign
            a = a.toLowerCase();
            // eslint-disable-next-line no-param-reassign
            b = b.toLowerCase();
        }
        if (a === b) return 1;

        const lenA = a.length;
        const lenB = b.length;
        const matchDistance = Math.floor(Math.max(lenA, lenB) / 2) - 1;

        const matchesA = new Array(lenA).fill(false);
        const matchesB = new Array(lenB).fill(false);

        // Count matches
        let matches = 0;
        for (let i = 0; i < lenA; i++) {
            const start = Math.max(0, i - matchDistance);
            const end = Math.min(i + matchDistance + 1, lenB);

            for (let j = start; j < end; j++) {
                // eslint-disable-next-line no-continue
                if (matchesB[j]) continue;
                // eslint-disable-next-line no-continue
                if (a[i] !== b[j]) continue;
                matchesA[i] = true;
                matchesB[j] = true;
                matches++;
                break;
            }
        }

        if (matches === 0) return 0;

        // Count transpositions
        let k = 0;
        let transpositions = 0;
        for (let i = 0; i < lenA; i++) {
            // eslint-disable-next-line no-continue
            if (!matchesA[i]) continue;
            while (!matchesB[k]) k++;
            if (a[i] !== b[k]) transpositions++;
            k++;
        }

        transpositions /= 2;

        // Jaro score
        const jaro = (matches / lenA + matches / lenB + (matches - transpositions) / matches) / 3;

        // Winkler prefix bonus
        let prefix = 0;
        for (let i = 0; i < Math.min(maxPrefix, lenA, lenB); i++) {
            if (a[i] === b[i]) prefix++;
            else break;
        }

        return jaro + prefix * prefixScale * (1 - jaro);
    }

    /* ---------- Month normalization ---------- */
    function normalizeMonth(value) {
        const v = value.trim().toLowerCase();
        if (!v) return null;

        if (/^\d+$/.test(v)) {
            const n = Number(v);
            return n >= 1 && n <= 12 ? n : null;
        }

        let best = null;
        let bestScore = 0;

        // eslint-disable-next-line no-restricted-syntax
        for (const m of months) {
            const score = jaroWinkler(v, m.name);
            if (score > bestScore) {
                bestScore = score;
                best = m;
            }
        }

        return bestScore > 0 ? best.index : null;
    }

    function daysInMonth(y, m) {
        return new Date(y, m, 0).getDate();
    }

    /* ---------- Blur handlers ---------- */
    function handleMonthBlur() {
        const normalized = normalizeMonth(month);
        if (normalized != null) {
            setMonth(String(normalized));
        }
    }

    function handleYearBlur() {
        const y = Number(year);
        const m = Number(month);
        const d = Number(day);
        if (!y || !m || !d) return;

        const max = daysInMonth(y, m);
        if (d > max) setDay(String(max));
    }

    /* ---------- Emit Date ---------- */
    useEffect(() => {
        // n
        if (!month || !day || !year) {
            onChange?.(null);
            return;
        }

        const y = Number(year);
        const m = Number(month);
        const d = Number(day);

        if (!y || !m || !d) {
            onChange?.(null);
            return;
        }

        const max = daysInMonth(y, m);
        if (d > max) {
            onChange?.(null);
            return;
        }

        onChange?.(new Date(y, m - 1, d));
    }, [month, day, year, onChange]);

    /* ---------- Render ---------- */
    return (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
            <label>
                Month
                <input
                    id="dob-month"
                    inputMode="text"
                    placeholder="Month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    onBlur={handleMonthBlur}
                    autoComplete="off"
                    style={{ width: '6em' }}
                    className="cta_input"
                />
            </label>

            <label>
                Day
                <input
                    id="dob-day"
                    type="number"
                    placeholder="Day"
                    min="1"
                    max="31"
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                    autoComplete="off"
                    style={{ width: '6em' }}
                    className="cta_input"
                />
            </label>

            <label>
                4-digit Year
                <input
                    id="dob-year"
                    type="number"
                    placeholder="Year"
                    value={year}
                    min={`${new Date().getFullYear() - 150}`}
                    max={`${new Date().getFullYear() - 15}`}
                    pattern="\d{4}"
                    onChange={(e) => setYear(e.target.value)}
                    onBlur={handleYearBlur}
                    autoComplete="off"
                    style={{ width: '6em' }}
                    className="cta_input"
                />
            </label>
        </div>
    );
}
